import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { compose } from 'core/libs/recompose';
import withBreakpoint from 'core/hocs/withBreakpoint';
import withTheme from 'core/hocs/withTheme';

import themePropTypes from 'core/utils/prop-types/theme';
import Link from 'core/components/Link';

import Close from 'core/components/Icon/CloseBold';

import styles from './index.styl';

const desktopWidth = 360;


function Notes(props) {
  const {
    description,
    href,
    text,
    theme,
    isDesktop,
  } = props;

  const [isOpened, setOpened] = useState(false);
  const [isPositionOnLeft, setPositionOnLeft] = useState(false);
  const anchor = useRef(null);
  const closeRef = useRef(null);
  const componentTheme = theme.controls.notes;

  const {
    background,
    borderColor,
    font,
    headlineFontFamily,
    linkType,
  } = componentTheme;

  const close = useCallback(event => {
    if (!anchor.current.contains(event.target)
    || event.target.parentNode.closest(`.${styles.close}`) === closeRef.current) {
      setOpened(false);
      document.removeEventListener('mousedown', close);
    }
  }, []);

  const open = useCallback(() => {
    setOpened(true);
    document.addEventListener('mousedown', close);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDesktop) {
      const parentNode = anchor.current?.parentNode;
      const parentRect = parentNode && parentNode.getBoundingClientRect() || {};
      const anchorRect = anchor.current && anchor.current.getBoundingClientRect() || {};
      const notesPositionRight = (anchorRect.right + desktopWidth) - anchorRect.width;
      const notesPositionLeft = anchorRect.right - desktopWidth;

      if ((notesPositionRight > parentRect.right) && (notesPositionLeft > parentRect.left)) {
        setPositionOnLeft(true);
      }
    }
  }, [isDesktop]);

  return (
    <button
      onClick={open}
      className={styles.btn}
      ref={anchor}
    >
      <style jsx>{`
        .${styles.btn}
        .${styles.notes}
          background ${background}

        .${styles.btn}
          border 1px solid ${borderColor}
          font ${font}

        .${styles.notes}
          :global(.desktop) &
            &._left
              left unset
              right 0

        .${styles.description}
          font-family ${headlineFontFamily}
      `}</style>
      {description}
      {isOpened &&
        <span className={cx(styles.notes, isPositionOnLeft && '_left')}>
          <span className={styles.description}>{description}</span>
          <span className={styles.text}>{text}</span>
          <Link
            to={href}
            className={styles.link}
            type={linkType}
          >
            <span className={styles.more}>Читать дальше</span>
          </Link>
          <span
            className={styles.close}
            onClick={close}
            ref={closeRef}
          >
            <Close />
          </span>
        </span>
      }
    </button>
  );
}

Notes.propTypes = {
  /** Ссылка "Читать дальше" на топик, содержащий информацию, на которую ссылкается заметка */
  href: PropTypes.string,
  /** Текст, отображаемый в кнопке и являющийся заголовком заметки */
  description: PropTypes.string.isRequired,
  /** Контент заметки */
  text: PropTypes.string.isRequired,
  /** @ignore */
  isDesktop: PropTypes.bool,
  /** @ignore */
  theme: themePropTypes(`
    {
      controls: {
        notes: {
          background,
          borderColor,
          linkType,
          headlineFontFamily,
          font,
        },
      },
    }
  `),
};

const NotesWithHOCs = compose(
  withBreakpoint,
  withTheme
)(Notes);

NotesWithHOCs.displayName = 'Notes';

export { Notes as StorybookComponent };
export default NotesWithHOCs;
